import React, {ComponentProps, useCallback, useEffect, useRef, useState} from 'react'
import {FieldRenderProps} from 'react-final-form'
import {useIntl} from 'react-intl'
import {TransitionGroup} from 'react-transition-group'
import Times from '../../../assets/icons/Times.svg'
import {monthAndDay, toClientDate, toServerDate} from '../../../helpers/dateUtil'
import Calendar, {CalendarViewType} from '../../common/Calendar'
import FadeTransition from '../../common/FadeTransition'
import LocaleText from '../../common/LocaleText'
import OutsideClickHandler from '../../containers/OutsideClickHandler'
import styles from './TopSearchDate.scss'

const TopSearchDate = (
  {input: {onChange, value},
    required,
    className,
    isBorder = true,
    onHover,
    isDisabled,
    alignRight,
    isClientDate,
    id,
    label,
    onMonthChange,
    minYear,
    maxYear,
    calendarOnTop,
    calendarClassName,
    calendarViewType = CalendarViewType.default
  }:
    FieldRenderProps<Date, HTMLInputElement> & {
    required?: boolean
    className?: string
    isBorder?: boolean
    onHover?: (show: boolean) => void
    isDisabled: ComponentProps<typeof Calendar>['isDisabled']
    onMonthChange: (date: Date) => any
    alignRight?: boolean
    isClientDate?: boolean
    id?: string
    label?: string
    minYear: number
    maxYear: number
    calendarOnTop: boolean
    calendarClassName?: string
    calendarViewType?: CalendarViewType
  }) => {
  const [open, setOpen] = useState(false)
  const intl = useIntl()
  const openCalender = useCallback(() => {
    setOpen(true)
    if (onHover) onHover(true)
  }, [])
  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  let dateValue: Date |　undefined
  if (value instanceof Date) dateValue = value
  else {
    const time = Date.parse(value)
    if (!isNaN(time) && isFinite(time)) dateValue = new Date(time)
  }
  if (dateValue && !isClientDate) dateValue = toClientDate(dateValue)
  const [showTimes, setShowTimes] = useState(false)
  const selectDay = useCallback((clientDate, e) => {
    onChange(isClientDate ? clientDate : toServerDate(clientDate))
    e.stopPropagation()
    setShowTimes(false)
    setOpen(false)
  }, [onChange, dateValue, isClientDate])
  const areaRef = useRef(null)
  const [inputElm, setInputElm] = useState()
  useEffect(() => void setInputElm(areaRef.current), [])
  const clear = useCallback((e?: MouseEvent) => {
    if (e) e.stopPropagation()
    if (onHover) onHover(true)
    onChange('')
    onClose()
  }, [onChange, onClose])
  return <div
    onClick={openCalender}
    ref={areaRef}
    onMouseEnter={() => {
      setShowTimes(true)
      if (onHover) onHover(true)
    }}
    onMouseLeave={() => {
      setShowTimes(false)
      if (onHover) onHover(false)
    }}
    className={['position-relative c-pointer w-100', styles.area, className].join(' ')}
  >
    <div className={[isBorder && 'border-right', 'mt-1', styles.border].filter(Boolean).join(' ')}>
      <div className={styles.searchTitle}>{label}</div>
      <div id={id} className={['c-pointer w-100 font-weight-bold mt-1'].filter(Boolean).join(' ')}>
        {dateValue ? intl.formatDate(dateValue, monthAndDay) : <LocaleText ja="日付を入力" en="Enter a date" />}
      </div>
      {
        <div className={
            ['position-absolute d-none', styles.clearArea, showTimes && value && 'd-block'].filter(Boolean).join(' ')
        }>
          <Times
            className={['c-pointer fa font-size-8', styles.clear].filter(Boolean).join(' ')}
            onClick={clear}
          />
        </div>
      }
    </div>
    <TransitionGroup component={null}>
      {open && <FadeTransition key="picker">
        <OutsideClickHandler
          excludedElms={[inputElm].filter(Boolean)}
          onOutsideClick={onClose}
          className={[
            !!calendarClassName && calendarClassName,
            styles.picker,
            'rounded shadow bg-white',
            alignRight && styles.alignRight,
            calendarOnTop && styles.calendarOnTop
          ].filter(Boolean).join(' ')}>
          <Calendar
            onChange={selectDay}
            value={dateValue}
            month={dateValue}
            isDisabled={isDisabled}
            onMonthChange={onMonthChange}
            minYear={minYear}
            maxYear={maxYear}
            calendarViewType={calendarViewType}
          />
        </OutsideClickHandler>
      </FadeTransition>}
    </TransitionGroup>
  </div>
}
TopSearchDate.displayName = 'DateField'
export default TopSearchDate
